<template>
    <div>
        <headline>
            User bearbeiten
            <template v-slot:more>
                <user-calendar v-show="editable" :user="user"></user-calendar>
                <user-delete v-show="editable" :user="user" @reload="reload()"></user-delete>
            </template>
        </headline>

        <alert-dialog v-show="!editable">
            Nutzer kann aufgrund fehlender Rechte nicht editiert werden.
        </alert-dialog>

        <full-card class="mb-8">

            <template v-slot:header>
                Persönliche Informationen
            </template>

            <input-text v-model="user.email" class="mb-2">
                E-Mail
            </input-text>

            <input-text v-model="user.alternative_email" class="mb-2">
                Alternative E-Mail
            </input-text>

            <input-text v-model="user.name" class="mb-2">
                Name
            </input-text>

            <input-text v-model="user.first_name" class="mb-2">
                Vorname
            </input-text>

            <input-text v-model="user.department" class="mb-2">
                Bereich
            </input-text>

            <input-text v-model="user.personnel_number" class="mb-2">
                Personal Nummer
            </input-text>

            <input-text v-model="user.priority" class="mb-4" type="number">
                <template v-slot:helper> Zahlen zwischen 1-5 sind erlaubt.</template>
                Priorität
            </input-text>

            <div class="sm:col-span-4 mb-4">
                <div v-if="!loading">
                    <select-menu v-model="user.desk_id"
                                 :values="desks.map(p => p.id)"
                                 :text="desks.map(p => p.name + (p.user ? (` (${p.user.first_name} ${p.user.name})`) : ''))">
                        Schreibtisch
                    </select-menu>
                    <button-secondary v-show="user.desk_id" @click="user.desk_id = null">
                        <i class="fa fa-trash mr-2"></i> Schreibtisch entziehen
                    </button-secondary>
                </div>

                <div class="mt-1 flex" v-show="user.desk_required === ''">
                    <span class="inline-flex items-center px-3 py-2 mr-6 text-red-500">
                        nicht konfiguriert
                    </span>
                    <button-secondary @click="user.desk_required = 'sometimes'">
                        Schreibtisch konfigurieren
                    </button-secondary>
                </div>
            </div>

            <warning class="mb-4" v-show="group.may_book_multiple_desks">
                Gruppenrichtlinie: Alle Nutzer dürfen mehrere Schreibtische pro Tag Buchen.
            </warning>

            <toggle-group class="mb-4" v-show="!group.may_book_multiple_desks" v-model="user.may_book_multiple_desks">
                Nutzer darf mehrere Schreibtische pro Tag buchen.
            </toggle-group>

            <toggle-group class="mb-4" v-model="user.ignore_scim" v-if="can.update.scim">
                Active Directory Verwaltung
                <template v-slot:helper>
                    Der Nutzer wird von der Active Directory Verwaltung ausgeschlossen
                </template>
            </toggle-group>

            <toggle-group class="mb-4" v-model="user.is_protected" v-if="user.login_method === 'saml' && group.has_protection_against_saml_changes">
                Benutzer ist geschützt
                <template v-slot:helper>
                    Wenn der Benutzer geschützt ist, dann werden keine zukünftige Änderungen durch externe SAML-Anmeldung vorgenommen
                </template>
            </toggle-group>

            <div class="mt-8 flex justify-end">
                <button-primary @click="updateUser" v-show="editable && !loading">
                    <i class="fa fa-save mr-2"></i> Speichern
                </button-primary>
                <loading v-show="loading"></loading>
            </div>
        </full-card>

        <full-card class="mb-8">
            <template v-slot:header>
                Usergruppe
            </template>
            <multi-select class="mb-2" v-model="userGroups"
                          :selectable="group.user_groups.map(g => ({id:g.id, text:g.name}))">
                Usergruppe
            </multi-select>

            <div class="mt-8 flex justify-end">
                <button-primary @click="updateUser" v-show="editable && !loading">
                    <i class="fa fa-save mr-2"></i> Speichern
                </button-primary>
                <loading v-show="loading"></loading>
            </div>
        </full-card>

        <full-card v-show="admin.is_admin" class="mb-8">
            <template v-slot:header>
                Userrechte bearbeiten
            </template>
            <permission-edit v-model="user"></permission-edit>
        </full-card>

        <user-avatar @updated="reload" v-model="user"></user-avatar>
    </div>
</template>

<script>
import ButtonSecondary from "./UI/ButtonSecondary";
import UserDelete from "./UserDelete";
import InputText from "./UI/InputText";
import Modal from "./Modal";
import TenantOrOwner from "./TenantOrOwner";
import ButtonPrimary from "./UI/ButtonPrimary";
import Card from "./UI/Card";
import SelectMenu from "./UI/SelectMenu";
import PermissionEdit from "./PermissionEdit";
import FullCard from "./UI/FullCard";
import includes from "lodash/includes";
import Loading from "./UI/Loading";
import MultiSelect from "./UI/MultiSelect";
import Alert from "./UI/Alert.vue";
import AlertDialog from "./UI/AlertDialog.vue";
import ButtonWhite from "./UI/ButtonWhite.vue";
import Headline from "./UI/Headline.vue";
import UserCalendar from "./User/UserCalendar.vue";
import UserAvatar from "./User/UserAvatar.vue";
import ToggleGroup from "./UI/ToggleGroup.vue";
import Warning from "./UI/Warning.vue";

export default {
    name: "UserEdit",
    components: {
        Warning,
        Alert,
        AlertDialog,
        ButtonPrimary,
        ButtonSecondary,
        ButtonWhite,
        Card,
        FullCard,
        Headline,
        InputText,
        Loading,
        Modal,
        MultiSelect,
        PermissionEdit,
        SelectMenu,
        TenantOrOwner,
        ToggleGroup,
        UserAvatar,
        UserCalendar,
        UserDelete,
    },
    props: ['user'],

    inject: ['admin', 'group', 'can'],

    data: () => ({
        userGroups: [],
        showModal: false,
        deskName: '',
        loading: false,
        desks: [],
    }),

    filters: {
        date(string) {
            if (string === undefined) {
                return '';
            }
            return string.substring(0, 10);
        },
    },

    computed: {
        editable() {
            if (this.admin.is_admin) {
                return true;
            }

            return this.admin.access.write.users.includes(this.user.id);
        },

        desk: {
            get() {
                if (!this.user.desk) {
                    return '';
                }
                return this.user.desk.name;
            },
        },
    },

    mounted() {
        this.loading = true;
        this.userGroups = this.user.user_groups.map(g => g.id);
        this.deskName = this.user?.desk?.name;
        this.$http.get('/admin/desks')
            .then(result => {
                this.desks = result.data.desks;
                this.loading = false;
            })
            .catch(error => {
                this.loading = false;
                this.$root.$emit('error', error);
            });
    },

    methods: {
        reload() {
            this.$emit('reload');
        },
        matches(value) {
            if (value === undefined) {
                return false;
            }

            if (this.search.length === 0) {
                return false;
            }

            return includes(value.toLowerCase(), this.search.toLowerCase());
        },
        updateUser() {
            this.loading = true;
            this.$http.post(`/admin/users/${this.user.id}`, {
                '_method': 'put',
                priority: this.user.priority,
                personnel_number: this.user.personnel_number,
                department: this.user.department,
                name: this.user.name,
                first_name: this.user.first_name,
                email: this.user.email,
                desk_required: this.user.desk_required,
                user_groups: this.userGroups,
                desk_id: this.user.desk_id,
                may_book_multiple_desks: this.user.may_book_multiple_desks,
                ignore_scim: this.user.ignore_scim,
                is_protected: this.user.is_protected,
            })
                .then(response => {
                    // todo emit new value!
                    //this.showModal = false;
                    this.reload();
                    this.user.is_configured = true;
                    this.user.priority = response.data.priority;
                    this.loading = false;
                    this.$root.$emit('alert', 'Nutzer gespeichert.');
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.loading = false;
                });
        },
    }
}
</script>

<style scoped>

</style>
